import React, { useState, useEffect } from 'react';
import { Button, CircularProgress, Box } from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';

// Helper function to get table data
const getTableData = () => {
  const table = document.querySelector('table');
  if (!table) return null;

  try {
    // Get headers
    const headers = [];
    const headerRow = table.querySelector('thead tr');
    if (headerRow) {
    Array.from(headerRow.querySelectorAll('th, td')).forEach((cell, index) => {
      // Extract plain text content, handling both <br> tags and HTML elements like <div>
      let text = '';
      
      
      // Check if this is a date column (skip Username and Project columns)
      if (index > 1) {
        // Handle different view types with different DOM structures
        
        // Check for Monthly/Yearly/Weekly All Views: div-based responsive design (check this FIRST)
        if (cell.innerHTML && (cell.innerHTML.includes('<div') || cell.innerHTML.includes('MuiBox-root'))) {
          // Handle multiple <div> elements from responsive design
          // First, check the actual DOM elements in the cell for visibility
          const actualDivElements = cell.querySelectorAll('div');
          let desktopDiv = null;
          
          if (actualDivElements.length > 0) {
            // First, try to find the div with the desktop CSS class (Monthly/Weekly All views)
            for (let div of actualDivElements) {
              if (div.className && div.className.includes('css-77pj5p')) {
                desktopDiv = div;
                break;
              }
            }
            
            // If no desktop div found by CSS class, look for the visible desktop div
            if (!desktopDiv) {
              for (let div of actualDivElements) {
                const computedStyle = window.getComputedStyle(div);
                const divHTML = div.innerHTML || '';
                const divText = (div.textContent || div.innerText || '').trim();
                
                // Check if this div is visible (desktop format) and contains proper content
                if (computedStyle.display !== 'none' && 
                    (divHTML.includes('<br>') || 
                     (divText.length > 3 && /[a-zA-Z]/.test(divText) && !/^\d+\/\d+$/.test(divText)))) {
                  desktopDiv = div;
                  break;
                }
              }
            }
          }
          
          // Extract text ONLY from the desktop div, handling <br> tags
          if (desktopDiv) {
            const divHTML = desktopDiv.innerHTML || '';
            if (divHTML.includes('<br>')) {
              // Handle <br> tags by replacing with space - handle both <br> and <br/>
              text = divHTML.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>/g, '').trim();
              // Clean up multiple spaces
              text = text.replace(/\s+/g, ' ');
            } else {
              text = desktopDiv.textContent || desktopDiv.innerText || '';
            }
          } else {
            // Fallback: parse HTML to find desktop format div
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = cell.innerHTML;
            const divElements = tempDiv.querySelectorAll('div');
            
            // For Weekly All views, look for div with <br> tag (desktop format)
            // For Yearly views, look for longer text content (desktop format)
            let bestDiv = null;
            
            // First try to find div with <br> tag (Weekly All views)
            for (let div of divElements) {
              const divHTML = div.innerHTML || '';
              if (divHTML.includes('<br>')) {
                bestDiv = div;
                break;
              }
            }
            
            // If no <br> div found, try yearly view logic
            if (!bestDiv) {
              let maxLength = 0;
              for (let div of divElements) {
                const divText = (div.textContent || '').trim();
                const divLength = divText.length;
                
                // Prioritize divs with longer text (desktop format)
                if (divLength > maxLength && 
                    div.style.display !== 'none' &&
                    (divText.includes('2024') || divText.includes('2025') || divText.includes('2026') || divLength > 5)) {
                  bestDiv = div;
                  maxLength = divLength;
                }
              }
              
              // Final fallback: use longest div
              if (!bestDiv) {
                let maxLength = 0;
                for (let div of divElements) {
                  const divText = (div.textContent || '').trim();
                  if (divText.length > maxLength) {
                    bestDiv = div;
                    maxLength = divText.length;
                  }
                }
              }
            }
            
            if (bestDiv) {
              const divHTML = bestDiv.innerHTML || '';
              if (divHTML.includes('<br>')) {
                text = divHTML.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>/g, '').trim();
                text = text.replace(/\s+/g, ' ');
              } else {
                text = bestDiv.textContent || bestDiv.innerText || '';
              }
            }
          }
        }
        // Check for Weekly View: direct <br> tags without div wrappers (single user weekly view)
        else if (cell.innerHTML && cell.innerHTML.includes('<br>')) {
          // Handle <br> tags by replacing with space
          text = cell.innerHTML.split('<br>').map(part => {
            const tempSpan = document.createElement('span');
            tempSpan.innerHTML = part.replace(/<[^>]*>/g, ''); // Remove any remaining HTML tags
            return (tempSpan.textContent || tempSpan.innerText || '').trim();
          }).filter(part => part !== '').join(' ');
        }
        // Fallback for simple text content
        else {
          text = cell.textContent || cell.innerText || '';
        }
      } else {
        // For Username and Project columns, use simple text extraction
        text = cell.textContent || cell.innerText || '';
      }
      
      headers.push(text?.trim() || '');
    });
    }

    // Get data rows
    const rows = [];
    const tbody = table.querySelector('tbody');
    if (!tbody) return null;

    const trElements = Array.from(tbody.querySelectorAll('tr'));
    
    // Process all rows
    trElements.forEach((currentRow, i) => {
      const cells = Array.from(currentRow.cells || []);
      if (cells.length === 0) return;

      // Get first cell text safely
      const firstCellText = cells[0]?.textContent?.trim() || '';
      const secondCellText = cells[1]?.textContent?.trim() || '';
      
      // Check if this is a comments row
      const isCommentsRow = firstCellText === '' && secondCellText === '';

      // Get the row's data with same logic as headers for responsive cells
      const rowData = Array.from(cells).map((cell, cellIndex) => {
        // Apply same responsive logic for Date Range column (index 1) and other date columns
        if (cellIndex >= 1 && cell.innerHTML && cell.innerHTML.includes('<div')) {
          
          // Handle responsive design in data cells
          const actualDivElements = cell.querySelectorAll('div');
          let desktopDiv = null;
          
          if (actualDivElements.length > 0) {
            // Find desktop div with css-77pj5p class
            for (let div of actualDivElements) {
              if (div.className && div.className.includes('css-77pj5p')) {
                desktopDiv = div;
                break;
              }
            }
            
            // If no desktop div found by CSS class, look for visible desktop div
            if (!desktopDiv) {
              for (let div of actualDivElements) {
                const computedStyle = window.getComputedStyle(div);
                const divHTML = div.innerHTML || '';
                const divText = (div.textContent || div.innerText || '').trim();
                
                if (computedStyle.display !== 'none' && 
                    (divHTML.includes('<br>') || 
                     (divText.length > 3 && /[a-zA-Z]/.test(divText) && !/^\d+\/\d+$/.test(divText)))) {
                  desktopDiv = div;
                  break;
                }
              }
            }
          }
          
          // Extract text from desktop div only
          if (desktopDiv) {
            const divHTML = desktopDiv.innerHTML || '';
            if (divHTML.includes('<br>')) {
              let text = divHTML.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>/g, '').trim();
              return text.replace(/\s+/g, ' ');
            } else {
              const text = desktopDiv.textContent || desktopDiv.innerText || '';
              return text;
            }
          }
        }
        
        // Fallback to simple text extraction
        return cell.textContent?.trim() || '';
      });

      // Add row to rows array
      if (isCommentsRow && rows.length > 0) {
        // Get the previous timecard row
        const previousRow = rows[rows.length - 1];
        
        // Create comments array with proper alignment
        const comments = Array(headers.length).fill('');
        
        // Map comments directly to their positions
        rowData.forEach((comment, index) => {
          if (comment) {
            comments[index] = comment;
          }
        });
        
        previousRow.comments = comments;
      } else if (!isCommentsRow) {
        // For regular rows, store values and initialize comments array
        rows.push({
          values: rowData,
          comments: Array(headers.length).fill('')
        });
      }
    });
    
    return { headers, rows };
  } catch (error) {
    console.error('Error getting table data:', error);
    return null;
  }
};

const ExportButton = ({ 
  viewType,
  timecards,
  isAllUsers,
  viewMode,
  user,
  startDate,
  endDate,
  date,
  disabled = false,
  mobileIconOnly = false
}) => {
  const [tableData, setTableData] = useState(null);

  useEffect(() => {
    // Force fresh table data extraction every time with aggressive cache busting
    setTableData(null);
    
    // Multiple timeouts to ensure DOM is fully updated
    setTimeout(() => {
      setTableData(null); // Clear again
    }, 50);
    
    setTimeout(() => {
      setTableData(getTableData());
    }, 150);
    
    // Additional cache bust after a longer delay
    setTimeout(() => {
      setTableData(getTableData());
    }, 300);
  }, [timecards, viewType, date, startDate, endDate]);
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    try {
      setLoading(true);

      // Force fresh table data extraction right before export
      const freshTableData = getTableData();
      console.log('Fresh table data for export:', freshTableData);

      // Use the fresh table data directly
      if (freshTableData) {
        // Use the original headers without comment columns
        const allHeaders = [...freshTableData.headers];
        
        const csvRows = [];

        // Process each row
        freshTableData.rows.forEach((row, rowIndex) => {
          // Add hours row
          const hoursValues = row.values.map(value => 
            value.includes(',') ? `"${value}"` : value
          );
          csvRows.push(hoursValues.join(','));

          // Add comments row if any comments exist
          if (row.comments.some(c => c !== '')) {
            csvRows.push(row.comments.join(','));
          }
        });

        const csvContent = csvRows.join('\n');

        // Get date range from the first timecard's values array
        const dateRangeIndex = freshTableData.headers.indexOf('Date Range');
        const dateRange = dateRangeIndex !== -1 ? freshTableData.rows[0].values[dateRangeIndex] : '';

        // Get filename based on view type and mode
        const userPart = viewMode === 'allProjects' ? 'all_projects' : (user?.username || 'all_users');
        let filename;
        
        if (viewType === 'weekly') {
          filename = `weekly-timecard-${userPart}-${dateRange.replace(' - ', '_to_')}.csv`;
        } else if (viewType === 'monthly') {
          // For monthly view, format the month and year from the date prop
          const monthYear = format(date, 'MMMM_yyyy', { locale: enUS });
          filename = `monthly-timecard-${userPart}-${monthYear}.csv`;
        } else if (viewType === 'yearly') {
          // For yearly view, use just the year from the date prop
          const year = format(date, 'yyyy', { locale: enUS });
          filename = `yearly-timecard-${userPart}_${year}.csv`;
        } else if (viewType === 'pto') {
          // For PTO view, use current export date
          const exportDate = format(new Date(), 'yyyy-MM-dd', { locale: enUS });
          filename = `pto-report-${exportDate}.csv`;
        }

        // Create the final CSV content with headers
        // Escape headers that might be interpreted as dates by Excel
        const escapedHeaders = allHeaders.map(header => {
          // If header contains month names and years, wrap in quotes to prevent Excel date conversion
          if (header.match(/^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}$/)) {
            return `"${header}"`;
          }
          return header;
        });
        
        const finalCsvContent = `${escapedHeaders.join(',')}\n${csvContent}`;
        
        // Create and download the file
        const blob = new Blob([finalCsvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting timecards:', error);
      // You might want to add a proper error notification system here
    } finally {
      setLoading(false);
    }
  };

  if (mobileIconOnly) {
    return (
      <Button
        variant="outlined"
        onClick={handleExport}
        disabled={disabled || loading || timecards.length === 0}
        sx={{
          minWidth: 'auto',
          padding: '8px',
          '@media (max-width:600px)': {
            padding: '6px',
            minWidth: '32px',
            height: '32px',
            transform: 'scale(0.85)', // Reduce scale for mobile
            '& .MuiSvgIcon-root': {
              fontSize: '1rem', // Smaller icon
            }
          },
          '@media (max-width:900px) and (orientation: landscape)': {
            padding: '6px',
            minWidth: '32px',
            height: '32px',
            transform: 'scale(0.85)', // Reduce scale for mobile landscape
            '& .MuiSvgIcon-root': {
              fontSize: '1rem', // Smaller icon
            }
          },
          '@media (min-width:601px)': {
            minWidth: 'auto',
            padding: '6px 16px',
          }
        }}
      >
        {loading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
        <Box 
          component="span" 
          sx={{ 
            display: 'none',
            '@media (min-width:601px)': {
              display: 'inline',
              marginLeft: '8px',
            },
            '@media (max-width:900px) and (orientation: landscape)': {
              display: 'none', // Hide label in mobile landscape orientation
            }
          }}
        >
          {loading ? 'Exporting...' : 'Export'}
        </Box>
      </Button>
    );
  }

  return (
    <Button
      variant="outlined"
      startIcon={loading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
      onClick={handleExport}
      disabled={disabled || loading || timecards.length === 0}
    >
      {loading ? 'Exporting...' : 'Export'}
    </Button>
  );
};

export default ExportButton;
